/**
 * 🔥 Hot Deals Section
 * Dedicated section for HOT and trending deals
 */

import React, { useState, useEffect } from 'react';
import SecretPropertyCard from './SecretPropertyCard';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase-config';

const HotDealsSection = ({ buyerId, maxDeals = 6 }) => {
  const [hotDeals, setHotDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, extreme, high, new

  useEffect(() => {
    loadHotDeals();
  }, [filter]);

  const loadHotDeals = async () => {
    setLoading(true);
    try {
      // Query properties marked as HOT
      let q = query(
        collection(db, 'properties'),
        where('isHotDeal', '==', true),
        where('hotExpiresAt', '>', new Date()),
        orderBy('hotScore', 'desc'),
        limit(maxDeals)
      );

      // Additional filters
      if (filter === 'extreme') {
        q = query(
          collection(db, 'properties'),
          where('isHotDeal', '==', true),
          where('hotLevel', '==', 'extreme'),
          orderBy('hotScore', 'desc'),
          limit(maxDeals)
        );
      } else if (filter === 'new') {
        q = query(
          collection(db, 'properties'),
          where('isHotDeal', '==', true),
          orderBy('hotMarkedAt', 'desc'),
          limit(maxDeals)
        );
      }

      const snapshot = await getDocs(q);
      const deals = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setHotDeals(deals);
    } catch (error) {
      console.error('Error loading hot deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { key: 'all', label: 'Összes', icon: '🔥' },
    { key: 'extreme', label: 'Extrém', icon: '🔥🔥🔥' },
    { key: 'high', label: 'Magas', icon: '🔥🔥' },
    { key: 'new', label: 'Új', icon: '🆕' }
  ];

  return (
    <section className="py-12 px-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-4xl animate-pulse">🔥</span>
              <h2 className="text-3xl font-black text-white">
                HOT Deals
              </h2>
            </div>
            <p className="text-gray-400">
              Alulárazott, gyorsan eladó, magas ROI ingatlanok. Ezek nem maradnak sokáig!
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`
                  px-4 py-2 rounded-full text-sm font-semibold transition-all
                  ${filter === f.key 
                    ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg shadow-red-500/30' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}
                `}
              >
                {f.icon} {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Urgency Banner */}
        <div className="mt-6 p-4 bg-gradient-to-r from-red-600/20 via-orange-600/20 to-red-600/20 border border-red-500/30 rounded-xl">
          <div className="flex items-center gap-4">
            <div className="text-3xl animate-bounce">⚡</div>
            <div>
              <p className="text-white font-bold">
                🔥 Szerezd meg előbb, mint a többiek!
              </p>
              <p className="text-sm text-red-300">
                A HOT deal-ek átlagosan 48 órán belül foglalásra kerülnek. Legyél gyors!
              </p>
            </div>
            <div className="ml-auto text-right">
              <div className="text-2xl font-black text-white">{hotDeals.length}</div>
              <div className="text-xs text-gray-400">Aktív HOT deal</div>
            </div>
          </div>
        </div>
      </div>

      {/* Deals Grid */}
      <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-96 bg-gray-800/50 rounded-2xl animate-pulse"></div>
            ))}
          </div>
        ) : hotDeals.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotDeals.map((property, index) => (
              <div 
                key={property.id}
                className={index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}
              >
                <SecretPropertyCard 
                  property={property}
                  buyerId={buyerId}
                  showMatchScore={true}
                  showRisk={true}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔥</div>
            <p className="text-xl text-gray-400">Jelenleg nincsenek aktív HOT deal-ek</p>
            <p className="text-sm text-gray-500 mt-2">
      Értesítünk, amint új HOT deal érkezik!
            </p>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="max-w-7xl mx-auto mt-12 text-center">
        <p className="text-gray-400 mb-4">
          Szeretnél elsőként értesülni a HOT deal-ekről?
        </p>
        <button className="px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white font-bold rounded-xl shadow-lg shadow-red-500/30 transition-all hover:scale-105">
          🔥 HOT Deal Alert Bekapcsolása
        </button>
      </div>
    </section>
  );
};

export default HotDealsSection;
